// "Where is the app open on Google Play": the production track's country list
// (read daily by the API, refreshable here) as a compact dropdown in the Apps
// row. Forbidden geos of the app are flagged so a clash is visible at once.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { refreshPlayCountries } from '@/api/apps';
import { COUNTRY_MAP } from '@/lib/countries';
import type { App } from '@/api/types';

export function playCountries(app: App): string[] | null {
  if (!app.play_countries_json) return null;
  try {
    const list = JSON.parse(app.play_countries_json);
    return Array.isArray(list) ? list.filter((c): c is string => typeof c === 'string') : null;
  } catch {
    return null;
  }
}

export function PlayCountriesDropdown({ app, forbidden }: { app: App; forbidden: string[] }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // The Apps table scrolls horizontally, which would clip an absolutely
  // placed panel; it is portalled to <body> and pinned under the button.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const countries = playCountries(app);

  useEffect(() => {
    if (!open) return;
    const rect = rootRef.current?.getBoundingClientRect();
    if (rect) setPos({ top: rect.bottom + 4, left: Math.max(8, Math.min(rect.left, window.innerWidth - 264)) });
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!rootRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    // A pinned panel would drift from its button on scroll; close instead.
    const onScroll = (e: Event) => { if (!panelRef.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const refresh = useMutation({
    mutationFn: () => refreshPlayCountries(app.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['apps'] }),
  });

  const forbiddenSet = useMemo(() => new Set(forbidden), [forbidden]);
  const clashes = (countries ?? []).filter((c) => forbiddenSet.has(c));
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (countries ?? [])
      .map((code) => ({ code, name: COUNTRY_MAP[code]?.name || code, flag: COUNTRY_MAP[code]?.flag || '' }))
      .filter((c) => !q || c.code.toLowerCase().includes(q) || c.name.toLowerCase().includes(q))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [countries, query]);

  // Not in any of our consoles: nothing to read.
  if (app.platform !== 'android' || !app.play_account_id) return null;

  const label = countries === null
    ? (app.play_countries_error ? 'Geo: error' : 'Geo: not read yet')
    : `Geo: ${countries.length}${app.play_countries_rest_of_world ? '+' : ''}`;

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
          clashes.length ? 'bg-danger-soft text-danger' : countries?.length ? 'bg-info-soft text-info' : 'bg-surface-muted text-content-subtle'
        }`}
        title={clashes.length ? `Open on Play in forbidden geos: ${clashes.join(', ')}` : 'Countries where the app is available on Google Play'}
      >
        {label}
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>

      {open && pos && createPortal(
        <div
          ref={panelRef}
          onClick={(e) => e.stopPropagation()}
          style={{ position: 'fixed', top: pos.top, left: pos.left }}
          className="z-50 w-64 rounded-md border border-border bg-surface shadow-lg"
        >
          <div className="flex items-center gap-2 border-b border-border p-2">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country"
              className="min-w-0 flex-1 rounded border border-border bg-surface-muted px-2 py-1 text-xs text-content"
            />
            <button
              type="button"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
              title="Re-read from Google Play now"
              className="p-1 rounded text-content-subtle hover:text-content hover:bg-surface-muted disabled:opacity-50"
            >
              <svg className={`w-3.5 h-3.5 ${refresh.isPending ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992V4.356M2.985 19.644v-4.992h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
            </button>
          </div>
          <div className="px-2 pt-1.5 text-[11px] text-content-subtle">
            {app.play_account_name && <>Console: {app.play_account_name} · </>}
            {app.play_countries_checked_at
              ? `checked ${new Date(app.play_countries_checked_at * 1000).toLocaleString()}`
              : 'not checked yet'}
            {!!app.play_countries_rest_of_world && <div>+ rest of world (new countries join automatically)</div>}
          </div>
          {(refresh.error || app.play_countries_error) && (
            <p className="px-2 pt-1 text-[11px] text-danger break-words">
              {refresh.error instanceof Error ? refresh.error.message : app.play_countries_error}
            </p>
          )}
          <ul className="max-h-60 overflow-y-auto py-1">
            {shown.map((c) => (
              <li key={c.code} className="flex items-center justify-between gap-2 px-2 py-0.5 text-xs text-content">
                <span className="truncate">{c.flag} {c.name}</span>
                <span className="flex items-center gap-1 shrink-0">
                  {forbiddenSet.has(c.code) && <span title="Forbidden geo for this app">⛔</span>}
                  <span className="font-mono text-content-subtle">{c.code}</span>
                </span>
              </li>
            ))}
            {countries !== null && shown.length === 0 && (
              <li className="px-2 py-2 text-xs text-content-subtle">{countries.length ? 'No match' : 'Not available in any country'}</li>
            )}
          </ul>
        </div>,
        document.body,
      )}
    </div>
  );
}
